import React, { useState, useEffect } from 'react';
import { useInvoice } from '../context/InvoiceContext';
import { useConfirm } from '../context/ConfirmContext';
import { apiService, creditService } from '../services/api';
import { formatCurrency, formatFileSize } from '../utils/formatCurrency';

const InvoiceEditModal = ({ invoice, onClose }) => {
  const { updateInvoice, currentUser } = useInvoice();
  const confirm = useConfirm();
  const [formData, setFormData] = useState({
    vendorName: invoice.vendorName || '',
    invoiceDate: invoice.invoiceDate || '',
    amount: invoice.amount || '',
    notes: invoice.notes || ''
  });
  const [gstIncluded, setGstIncluded] = useState(invoice.gstIncluded || false);
  const [paymentMethod, setPaymentMethod] = useState(invoice.paymentMethod || 'standard');
  const [liveBalance, setLiveBalance] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState(invoice.attachments || []);
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  // Credit balance must reflect the invoice's owner, not whoever is editing it
  // (an admin editing someone else's invoice should see/spend that person's balance).
  const invoiceOwnerId = invoice.uploadedBy || invoice.uploader?.id;
  const fallbackOwnerBalance = invoiceOwnerId === currentUser?.id
    ? parseFloat(currentUser?.creditBalance || 0)
    : parseFloat(invoice.uploader?.creditBalance || 0);

  useEffect(() => {
    if (invoiceOwnerId) {
      creditService.getTransactions(invoiceOwnerId)
        .then(data => { if (data.balance !== undefined) setLiveBalance(parseFloat(data.balance)); })
        .catch(() => {});
    }
  }, [invoiceOwnerId]);

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleAddAttachment = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'].includes(file.type)) {
      alert('Please upload only PNG, JPG images or PDF files');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }
    setAttachmentUploading(true);
    try {
      const attachment = await apiService.addAttachment(invoice.id, file);
      if (attachment) setAttachments(prev => [...prev, attachment]);
    } catch (error) {
      alert(error.message || 'Failed to add attachment');
    } finally {
      setAttachmentUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    const ok = await confirm('Are you sure you want to delete this attachment?', {
      title: 'Delete Attachment',
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    try {
      await apiService.deleteAttachment(invoice.id, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (error) {
      // error is shown via notification in context
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'].includes(selectedFile.type)) {
      alert('Please upload only PNG, JPG images or PDF files');
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }
    setFile(selectedFile);
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    let payload;
    if (file) {
      payload = new FormData();
      payload.append('file', file);
      payload.append('vendorName', formData.vendorName);
      payload.append('invoiceDate', formData.invoiceDate);
      payload.append('amount', formData.amount);
      payload.append('notes', formData.notes);
      payload.append('gstIncluded', gstIncluded);
      payload.append('paymentMethod', paymentMethod);
    } else {
      payload = { ...formData, gstIncluded, paymentMethod };
    }

    const success = await updateInvoice(invoice.id, payload);
    setIsLoading(false);
    if (success) onClose();
  };

  const currentFileUrl = invoice.filename
    ? `${process.env.REACT_APP_API_URL.replace('/api', '')}/uploads/${invoice.filename}`
    : null;
  const currentIsPDF = invoice.mimeType === 'application/pdf';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex-between mb-lg">
          <h3>Edit Invoice</h3>
          <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Invoice File (PNG, JPG, PDF)</label>
            {currentFileUrl && !file && (
              <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                {currentIsPDF ? (
                  <div style={{ padding: 'var(--spacing-sm)', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}>
                    📄 {invoice.originalName}
                    {invoice.fileSize ? <span style={{ marginLeft: '6px', color: 'var(--color-gray-400)' }}>({formatFileSize(invoice.fileSize)})</span> : null}
                  </div>
                ) : (
                  <img
                    src={currentFileUrl}
                    alt="Current invoice"
                    style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: 'var(--radius-sm)', objectFit: 'contain' }}
                  />
                )}
              </div>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,application/pdf"
              onChange={handleFileChange}
              className="form-input"
            />
            {preview && (
              <img
                src={preview}
                alt="New file preview"
                style={{ maxWidth: '100%', maxHeight: '150px', marginTop: 'var(--spacing-sm)', borderRadius: 'var(--radius-sm)', objectFit: 'contain' }}
              />
            )}
            {file && file.type === 'application/pdf' && (
              <div style={{ marginTop: 'var(--spacing-sm)', padding: 'var(--spacing-sm)', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}>
                📄 {file.name}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Additional Attachments</label>
            {attachments.length > 0 && (
              <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                {attachments.map(att => (
                  <div key={att.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--spacing-xs) var(--spacing-sm)', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', marginBottom: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)' }}>
                    <span>📎 {att.originalName}{att.fileSize ? <span style={{ marginLeft: '6px', color: 'var(--color-gray-400)' }}>({formatFileSize(att.fileSize)})</span> : null}</span>
                    <button type="button" onClick={() => handleDeleteAttachment(att.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontSize: 'var(--font-size-sm)', padding: '2px 6px' }}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <input type="file" accept="image/png,image/jpeg,image/jpg,application/pdf" onChange={handleAddAttachment} className="form-input" disabled={attachmentUploading} />
            {attachmentUploading && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)', marginTop: 'var(--spacing-xs)' }}>Uploading...</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Vendor Name</label>
            <input type="text" name="vendorName" className="form-input" value={formData.vendorName} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label className="form-label">Invoice Date</label>
            <input type="date" name="invoiceDate" className="form-input" value={formData.invoiceDate} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label className="form-label">Amount</label>
            <input type="number" name="amount" className="form-input" value={formData.amount} onChange={handleChange} step="0.01" min="0" required />
          </div>

          <div className="form-group">
            {(() => {
              const balance = liveBalance !== null ? liveBalance : fallbackOwnerBalance;
              // When editing, if already paid from credit, the balance "available" should include that
              // amount back (since it was already deducted). Show balance + original if method was credit.
              const effectiveBalance = invoice.paymentMethod === 'credit_balance'
                ? balance + parseFloat(invoice.amount || 0)
                : balance;
              return (
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={paymentMethod === 'credit_balance'}
                    onChange={e => setPaymentMethod(e.target.checked ? 'credit_balance' : 'standard')}
                    style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary)' }}
                  />
                  <span className="form-label" style={{ margin: 0 }}>Paid from Credit Balance</span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginLeft: 'var(--spacing-xs)' }}>
                    ₹{effectiveBalance.toFixed(2)}
                  </span>
                </label>
              );
            })()}
            {paymentMethod === 'credit_balance' && formData.amount && (
              <div style={{ marginTop: 'var(--spacing-xs)', fontSize: 'var(--font-size-sm)', paddingLeft: '26px' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  Balance after: ₹{(
                    (invoice.paymentMethod === 'credit_balance'
                      ? (liveBalance !== null ? liveBalance : fallbackOwnerBalance) + parseFloat(invoice.amount || 0)
                      : (liveBalance !== null ? liveBalance : fallbackOwnerBalance)
                    ) - parseFloat(formData.amount)
                  ).toFixed(2)}
                </span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={gstIncluded}
                onChange={(e) => setGstIncluded(e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary)', flexShrink: 0 }}
              />
              <span className="form-label" style={{ margin: 0 }}>Amount includes 18% GST</span>
            </label>
            {gstIncluded && formData.amount && (
              <div style={{ marginTop: 'var(--spacing-xs)', padding: 'var(--spacing-sm)', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-secondary)' }}>
                Base: {formatCurrency(parseFloat(formData.amount) / 1.18)} + GST: {formatCurrency(parseFloat(formData.amount) - parseFloat(formData.amount) / 1.18)} = Total: {formatCurrency(formData.amount)}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea name="notes" className="form-input" value={formData.notes} onChange={handleChange} rows="3" />
          </div>

          <div className="flex gap-md">
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceEditModal;
